import { Link } from '@tanstack/react-router';
import { availableIn, capabilityOf, planName, type CapabilityId } from '@dale/plans';

import { insideNativeApp } from '../auth/native-app';
import { isOwner } from '../auth/roles';
import { useSession } from '../auth/useSession';

/** The shape of the faded screen behind the lock: roughly what the section looks like once it opens. */
type PreviewShape = 'filas' | 'grafica' | 'tarjetas' | 'plano' | 'horario' | 'chat';

const SHAPE: Readonly<Record<CapabilityId, PreviewShape>> = {
  inicio: 'grafica',
  clientes: 'filas',
  cobrar: 'filas',
  equipo: 'filas',
  accesos: 'filas',
  clases: 'horario',
  planes: 'tarjetas',
  perfiles: 'filas',
  ofertas: 'tarjetas',
  finanzas: 'grafica',
  facturas: 'filas',
  historial: 'grafica',
  mesas: 'plano',
  comandas: 'plano',
  menu: 'tarjetas',
  inteligencia: 'grafica',
  sucursales: 'tarjetas',
  agentes: 'tarjetas',
  recepcionista: 'chat',
};

/**
 * A section the business's plan does not include, shown where the section
 * would be instead of bouncing home: a faded glimpse of it, what it does in
 * three lines, and the plan that opens it at its price. The owner can change
 * plan from here (Membresía opens on that plan); a teammate is told who can.
 */
export function PlanLocked({ capability }: { capability: CapabilityId }) {
  const { session } = useSession();
  const inApp = insideNativeApp(session);
  const { title, lines, minPlan } = capabilityOf(capability);
  return (
    <main className="shell-main plan-locked-screen">
      <section className="plan-locked" aria-labelledby="plan-locked-title">
        <LockedPreview shape={SHAPE[capability]} />
        <div className="plan-locked-card">
          <p className="plan-locked-badge">
            <LockGlyph />
            <span>{planName(minPlan)}</span>
          </p>
          <h1 className="plan-locked-title" id="plan-locked-title">
            {title}
          </h1>
          <ul className="plan-locked-lines">
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
          {inApp ? null : <p className="plan-locked-price">{availableIn(capability)}</p>}
          {inApp ? null : isOwner(session?.role) ? (
            <Link to="/membresia" search={{ plan: minPlan }} className="btn plan-locked-cta">
              Cambiar a {planName(minPlan)}
            </Link>
          ) : (
            <p className="plan-locked-ask">Pídeselo a la persona dueña del negocio.</p>
          )}
        </div>
      </section>
    </main>
  );
}

/**
 * The same lock, small, for a part of a page the plan does not include (Qué
 * ve cada perfil inside Equipo): its name, what it does, the plan that opens
 * it, and the way there. The rest of the page keeps working around it.
 */
export function LockedSection({ capability }: { capability: CapabilityId }) {
  const { session } = useSession();
  const inApp = insideNativeApp(session);
  const { title, lines, minPlan } = capabilityOf(capability);
  const titleId = `locked-${capability}`;
  return (
    <section className="section locked-section" aria-labelledby={titleId}>
      <h2 className="section-label" id={titleId}>
        {title}
      </h2>
      <div className="locked-section-card">
        <p className="plan-locked-badge">
          <LockGlyph />
          <span>{planName(minPlan)}</span>
        </p>
        <p className="locked-section-line">{lines[0]}</p>
        {inApp ? null : <p className="locked-section-price">{availableIn(capability)}</p>}
        {inApp ? null : isOwner(session?.role) ? (
          <Link to="/membresia" search={{ plan: minPlan }} className="btn small secondary">
            Cambiar a {planName(minPlan)}
          </Link>
        ) : (
          <p className="plan-locked-ask">Pídeselo a la persona dueña del negocio.</p>
        )}
      </div>
    </section>
  );
}

/** The small padlock the nav and the lock share. */
export function LockGlyph({ className }: { className?: string }) {
  return (
    <svg className={className ?? 'lock-glyph'} viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
      <path
        d="M5 7V5a3 3 0 0 1 6 0v2m-7.25 0h8.5c.41 0 .75.34.75.75v5.5c0 .41-.34.75-.75.75h-8.5a.75.75 0 0 1-.75-.75v-5.5c0-.41.34-.75.75-.75Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const BARS = [38, 52, 44, 66, 58, 74, 62, 81, 70, 88];

/** Decorative only: hidden from assistive tech, it carries no data. */
function LockedPreview({ shape }: { shape: PreviewShape }) {
  return (
    <div className={`plan-locked-preview plan-locked-preview--${shape}`} aria-hidden="true">
      {shape === 'grafica' ? (
        <>
          <div className="ghost-kpis">
            <span className="ghost-tile" />
            <span className="ghost-tile" />
            <span className="ghost-tile" />
          </div>
          <div className="ghost-chart">
            {BARS.map((height, index) => (
              <span key={index} className="ghost-bar" style={{ height: `${height}%` }} />
            ))}
          </div>
        </>
      ) : null}
      {shape === 'filas'
        ? [0, 1, 2, 3, 4].map((row) => (
            <div key={row} className="ghost-row">
              <span className="ghost-dot" />
              <span className="ghost-lines">
                <span className="ghost-line" />
                <span className="ghost-line short" />
              </span>
              <span className="ghost-pill" />
            </div>
          ))
        : null}
      {shape === 'tarjetas' ? (
        <div className="ghost-cards">
          {[0, 1, 2, 3, 4, 5].map((card) => (
            <span key={card} className="ghost-card">
              <span className="ghost-media" />
              <span className="ghost-line" />
              <span className="ghost-line short" />
            </span>
          ))}
        </div>
      ) : null}
      {shape === 'plano' ? (
        <div className="ghost-floor">
          {[0, 1, 2, 3, 4, 5, 6, 7].map((table) => (
            <span key={table} className={table % 3 === 0 ? 'ghost-table taken' : 'ghost-table'} />
          ))}
        </div>
      ) : null}
      {shape === 'horario' ? (
        <div className="ghost-week">
          {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map((slot) => (
            <span key={slot} className={slot % 4 === 1 ? 'ghost-slot empty' : 'ghost-slot'} />
          ))}
        </div>
      ) : null}
      {shape === 'chat' ? (
        <div className="ghost-chat">
          {['in', 'out', 'in', 'out', 'in'].map((side, index) => (
            <span key={index} className={`ghost-bubble ${side}`} />
          ))}
        </div>
      ) : null}
    </div>
  );
}
